import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onBeforeUnmount } from 'vue';

interface Coords {
    x: number,
    y: number
}

interface Size {
    w: number,
    h: number
}

interface DraggingStartState {
    page: Coords,
    translate: Coords
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PannableViewport',
  emits: ['click'],
  setup(__props, { expose, emit }) {



const viewportElement = ref<HTMLElement | undefined>();

const translate = ref<Coords>({
    x: 0,
    y: 0
});

const translateCSS = computed<string>(() => {
    return `translate(${translate.value.x}px, ${translate.value.y}px)`;
});

const disableTransition = ref(false);

const contentStyle = computed(() => ({
    transform: translateCSS.value,
    width: '100%',
    height: '100%',
    transition: disableTransition.value ? '' : 'transform 0.2s ease-out'
}));

const viewportSize = computed<Size>(() => {
    if (!viewportElement.value) {
        return { w: 0, h: 0 };
    }
    const element = viewportElement.value as HTMLElement;
    return {
        w: element.clientWidth,
        h: element.clientHeight
    };
});

const contentSize = computed<Size>(() => {
    if (!viewportElement.value) {
        return { w: 0, h: 0 };
    }
    const contentSizeElement = viewportElement.value.firstElementChild as HTMLElement;
    return contentSizeElement && contentSizeElement.firstElementChild
        ? {
            w: (contentSizeElement.firstElementChild as HTMLElement).clientWidth,
            h: (contentSizeElement.firstElementChild as HTMLElement).clientHeight
        }
        : {
            w: viewportSize.value.w * 2,
            h: viewportSize.value.h * 2
        };
});

const enforceTranslateBounds = (coords: Coords): Coords => {
    const minX = -(contentSize.value.w - viewportSize.value.w);
    const maxX = 0;
    const minY = -(contentSize.value.h - viewportSize.value.h);
    const maxY = 0;
    return {
        x: Math.max(minX, Math.min(maxX, coords.x)),
        y: Math.max(minY, Math.min(maxY, coords.y))
    };
};

// Pan the content to where it needs to be, when mouse is hovered on (x, y) on viewport
const hoverPan = (hoverX: number, hoverY: number, disablePanningTransition = false) => {
    if (!viewportElement.value) {
        return;
    }

    const pct = {
        x: hoverX / viewportSize.value.w,
        y: hoverY / viewportSize.value.h
    };
    if (disablePanningTransition) {
        disableTransition.value = true;
    }
    window.requestAnimationFrame(() => {
        translate.value = {
            x: -(contentSize.value.w - viewportSize.value.w) * pct.x,
            y: -(contentSize.value.h - viewportSize.value.h) * pct.y
        };
        window.setTimeout(() => {
            disableTransition.value = false;
        }, 100);
        //
    });
};

const clickPan = (clickX: number, clickY: number) => {
    hoverPan(clickX, clickY, true);
};

let pointerIsDown = false;

const handleMouseMove = (event: MouseEvent) => {
    if (pointerIsDown) {
        return;
    }
    const element = event.currentTarget as HTMLElement;
    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    hoverPan(x, y);
};

let draggingStartState: DraggingStartState | undefined;

const handlePointerMove = (event: PointerEvent) => {
    // event.preventDefault();
    event.stopPropagation();
    if (!draggingStartState) {
        return;
    }
    const delta:Coords = {
        x: event.pageX - draggingStartState.page.x,
        y: event.pageY - draggingStartState.page.y
    };
    window.requestAnimationFrame(() => {
        if (!draggingStartState) {
            return;
        }
        translate.value = enforceTranslateBounds(
            {
                x: draggingStartState.translate.x + delta.x,
                y: draggingStartState.translate.y + delta.y
            }
        );
    });
};

const handlePointerUp = (event: PointerEvent) => {
    event.preventDefault();
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    if (!draggingStartState) {
        return;
    }
    pointerIsDown = false;

    const delta:Coords = {
        x: event.pageX - draggingStartState.page.x,
        y: event.pageY - draggingStartState.page.y
    };
    if (Math.abs(delta.x) < 5 && Math.abs(delta.y) < 5) {
        emit('click', event as MouseEvent);
    }
};

const handlePointerDown = (event: PointerEvent) => {
    event.stopPropagation();

    pointerIsDown = true;

    draggingStartState = {
        page: {
            x: event.pageX,
            y: event.pageY
        },
        translate: {
            x: translate.value.x,
            y: translate.value.y
        }
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
};

onBeforeUnmount(() => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
});

expose({
    clickPan
});

return { __sfc: true,emit, viewportElement, translate, translateCSS, disableTransition, contentStyle, viewportSize, contentSize, enforceTranslateBounds, hoverPan, clickPan, pointerIsDown, handleMouseMove, draggingStartState, handlePointerMove, handlePointerUp, handlePointerDown }
}

})